import { useState, useEffect } from 'react';

interface AdminUser {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string;
  createdAt: string;
  lastLogin: string;
  apiKeysCount: number;
  totalRequests: number;
  requestsThisMonth: number;
  mockEndpointsCount: number;
}

interface PlatformStats {
  totalUsers: number;
  totalApiKeys: number;
  totalRequests: number;
  requestsThisMonth: number;
  totalMockEndpoints: number;
  activeUsers7Days: number;
  requestsToday: number;
  avgRequestsPerUser: number;
}

interface RequestLog {
  id: string;
  endpoint: string;
  ipAddress: string;
  timestamp: string;
  responseTimeMs: number | null;
  statusCode: number | null;
  userName: string | null;
  userEmail: string | null;
  userId: string | null;
}

interface EndpointStats {
  endpoint: string;
  count: number;
  avgResponseTime: number;
}

interface DailyStats {
  date: string;
  requests: number;
}

interface StatusDistribution {
  statusCode: number;
  count: number;
}

interface ResponseTimePoint {
  date: string;
  avgResponseTime: number;
  p50ResponseTime: number;
  p95ResponseTime: number;
  p99ResponseTime: number;
  requestCount: number;
}

interface PerformanceMetrics {
  daily: ResponseTimePoint[];
  weekly: ResponseTimePoint[];
  monthly: ResponseTimePoint[];
  allTime: {
    avgResponseTime: number;
    p50ResponseTime: number;
    p95ResponseTime: number;
    p99ResponseTime: number;
    minResponseTime: number;
    maxResponseTime: number;
    totalRequests: number;
  };
}

type AdminTab = 'overview' | 'users' | 'logs';
type SortColumn = 'timestamp' | 'response_time_ms' | 'status_code' | 'endpoint';
type SortOrder = 'asc' | 'desc';
type PerfPeriod = 'daily' | 'weekly' | 'monthly' | 'allTime';

interface DashboardAdminViewProps {
  userId: string;
}

export default function DashboardAdminView({ userId }: DashboardAdminViewProps) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [logs, setLogs] = useState<RequestLog[]>([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [endpointStats, setEndpointStats] = useState<EndpointStats[]>([]);
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  const [statusDistribution, setStatusDistribution] = useState<StatusDistribution[]>([]);
  const [performanceMetrics, setPerformanceMetrics] = useState<PerformanceMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<AdminTab>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [logsPage, setLogsPage] = useState(0);
  const [sortBy, setSortBy] = useState<SortColumn>('timestamp');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [perfPeriod, setPerfPeriod] = useState<PerfPeriod>('weekly');
  const logsPerPage = 50;

  useEffect(() => {
    fetchAdminData();
  }, [userId, selectedUserId, logsPage, sortBy, sortOrder]);

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (selectedUserId) params.append('userId', selectedUserId);
      params.append('limit', logsPerPage.toString());
      params.append('offset', (logsPage * logsPerPage).toString());
      params.append('sortBy', sortBy);
      params.append('sortOrder', sortOrder);

      const response = await fetch(`/api/dashboard/admin?${params.toString()}`);

      if (!response.ok) {
        if (response.status === 403) {
          setError('Access denied. Admin privileges required.');
        } else {
          setError('Failed to load admin data');
        }
        return;
      }

      const data = await response.json();
      setUsers(data.users || []);
      setStats(data.stats || null);
      setLogs(data.logs || []);
      setTotalLogs(data.totalLogs || 0);
      setEndpointStats(data.endpointStats || []);
      setDailyStats(data.dailyStats || []);
      setStatusDistribution(data.statusDistribution || []);
      setPerformanceMetrics(data.performanceMetrics || null);
    } catch (err) {
      console.error('Failed to fetch admin data:', err);
      setError('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: SortColumn) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setLogsPage(0);
  };

  const getSortIcon = (column: SortColumn) => {
    if (sortBy !== column) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    );
  };

  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusColor = (statusCode: number | null) => {
    if (!statusCode) return 'bg-gray-100 text-gray-800';
    if (statusCode >= 200 && statusCode < 300) return 'bg-green-100 text-green-800';
    if (statusCode >= 300 && statusCode < 400) return 'bg-blue-100 text-blue-800';
    if (statusCode >= 400 && statusCode < 500) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const maxDailyRequests = Math.max(...dailyStats.map(d => d.requests), 1);

  // Get performance data based on selected period
  const getPerfData = () => {
    if (!performanceMetrics) return [];
    if (perfPeriod === 'allTime') return [];
    return performanceMetrics[perfPeriod];
  };

  const perfData = getPerfData();
  const maxPerfValue = Math.max(...perfData.map(d => d.p95ResponseTime), 1);

  if (loading && !stats) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-coral-500 border-t-transparent"></div>
        <p className="mt-4 text-gray-600">Loading admin panel...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 max-w-md mx-auto">
          <svg className="w-12 h-12 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-red-700 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Admin Panel</h2>
            <p className="text-gray-600">Platform management, logs, and usage metrics</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 font-medium rounded-t-lg transition-colors ${
            activeTab === 'overview'
              ? 'bg-purple-100 text-purple-700 border-b-2 border-purple-500'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
          }`}
        >
          Overview
        </button>
        <button
          onClick={() => setActiveTab('users')}
          className={`px-4 py-2 font-medium rounded-t-lg transition-colors ${
            activeTab === 'users'
              ? 'bg-purple-100 text-purple-700 border-b-2 border-purple-500'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
          }`}
        >
          Users
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`px-4 py-2 font-medium rounded-t-lg transition-colors ${
            activeTab === 'logs'
              ? 'bg-purple-100 text-purple-700 border-b-2 border-purple-500'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
          }`}
        >
          Logs
        </button>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && stats && (
        <div>
          {/* Platform Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-100 rounded-xl">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Users</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalUsers.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-teal-100 rounded-xl">
                  <svg className="w-6 h-6 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total API Keys</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalApiKeys.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-coral-100 rounded-xl">
                  <svg className="w-6 h-6 text-coral-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Requests</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalRequests.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-orange-100 rounded-xl">
                  <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Requests Today</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.requestsToday.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-pink-100 rounded-xl">
                  <svg className="w-6 h-6 text-pink-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Requests This Month</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.requestsThisMonth.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-indigo-100 rounded-xl">
                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Mock Endpoints</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalMockEndpoints.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-xl">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728m-9.9-2.829a5 5 0 010-7.07m7.072 0a5 5 0 010 7.07M13 12a1 1 0 11-2 0 1 1 0 012 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Active Users (7 days)</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.activeUsers7Days.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Avg Requests/User</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.avgRequestsPerUser.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Daily Request Trend Chart */}
          {dailyStats.length > 0 && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Request Trend (Last 14 Days)</h3>
              <div className="flex items-end gap-1 h-40">
                {dailyStats.map((day, index) => (
                  <div key={index} className="flex-1 flex flex-col items-center">
                    <div
                      className="w-full bg-gradient-to-t from-purple-500 to-indigo-500 rounded-t transition-all hover:from-purple-600 hover:to-indigo-600"
                      style={{ height: `${(day.requests / maxDailyRequests) * 100}%`, minHeight: day.requests > 0 ? '4px' : '0' }}
                      title={`${new Date(day.date).toLocaleDateString()}: ${day.requests} requests`}
                    />
                    <p className="text-xs text-gray-500 mt-2 transform -rotate-45 origin-top-left">
                      {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Two Column Layout for Endpoint Stats and Status Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            {/* Top Endpoints */}
            {endpointStats.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Endpoints (30 days)</h3>
                <div className="space-y-3">
                  {endpointStats.map((ep, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-mono text-gray-700 truncate">{ep.endpoint}</p>
                        <div className="w-full bg-gray-100 rounded-full h-2 mt-1">
                          <div
                            className="bg-gradient-to-r from-purple-500 to-indigo-500 h-2 rounded-full"
                            style={{ width: `${(ep.count / endpointStats[0].count) * 100}%` }}
                          />
                        </div>
                      </div>
                      <div className="ml-4 text-right flex-shrink-0">
                        <p className="text-sm font-semibold text-gray-900">{ep.count.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">{ep.avgResponseTime}ms avg</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Status Code Distribution */}
            {statusDistribution.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Status Code Distribution (30 days)</h3>
                <div className="space-y-3">
                  {statusDistribution.map((status, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status.statusCode)}`}>
                          {status.statusCode}
                        </span>
                        <span className="text-sm text-gray-600">
                          {status.statusCode >= 200 && status.statusCode < 300 && 'Success'}
                          {status.statusCode >= 300 && status.statusCode < 400 && 'Redirect'}
                          {status.statusCode >= 400 && status.statusCode < 500 && 'Client Error'}
                          {status.statusCode >= 500 && 'Server Error'}
                        </span>
                      </div>
                      <span className="text-sm font-semibold text-gray-900">{status.count.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <h3 className="text-lg font-semibold text-gray-900">All Users ({users.length})</h3>
              <div className="relative">
                <svg className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent w-full sm:w-64"
                />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">API Keys</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mock Endpoints</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Requests</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">This Month</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Active</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                      {searchQuery ? 'No users found matching your search' : 'No users yet'}
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          {user.avatarUrl ? (
                            <img
                              src={user.avatarUrl}
                              alt={user.name}
                              className="w-10 h-10 rounded-full"
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-coral-500 to-teal-500 flex items-center justify-center text-white font-semibold">
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-gray-900">{user.name}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                          {user.apiKeysCount} keys
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                          {user.mockEndpointsCount} mocks
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900 font-medium">
                        {user.totalRequests.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                        {user.requestsThisMonth.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(user.lastLogin).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => {
                            setSelectedUserId(user.id);
                            setLogsPage(0);
                            setActiveTab('logs');
                          }}
                          className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                        >
                          View Logs
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {filteredUsers.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
              <p className="text-sm text-gray-600">
                Showing {filteredUsers.length} of {users.length} users
              </p>
            </div>
          )}
        </div>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <div>
          {/* Performance Metrics Section */}
          {performanceMetrics && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <h3 className="text-lg font-semibold text-gray-900">API Response Time Performance</h3>
                <div className="flex gap-2">
                  {(['daily', 'weekly', 'monthly', 'allTime'] as PerfPeriod[]).map((period) => (
                    <button
                      key={period}
                      onClick={() => setPerfPeriod(period)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                        perfPeriod === period
                          ? 'bg-purple-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {period === 'daily' && '24h'}
                      {period === 'weekly' && '7d'}
                      {period === 'monthly' && '30d'}
                      {period === 'allTime' && 'All Time'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Summary Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4">
                  <p className="text-xs text-purple-600 font-medium mb-1">Avg Response</p>
                  <p className="text-2xl font-bold text-purple-900">
                    {perfPeriod === 'allTime'
                      ? performanceMetrics.allTime.avgResponseTime
                      : perfData.length > 0
                      ? Math.round(perfData.reduce((a, b) => a + b.avgResponseTime, 0) / perfData.length)
                      : 0}
                    <span className="text-sm font-normal">ms</span>
                  </p>
                </div>
                <div className="bg-gradient-to-br from-teal-50 to-green-50 rounded-xl p-4">
                  <p className="text-xs text-teal-600 font-medium mb-1">P50 (Median)</p>
                  <p className="text-2xl font-bold text-teal-900">
                    {perfPeriod === 'allTime'
                      ? performanceMetrics.allTime.p50ResponseTime
                      : perfData.length > 0
                      ? Math.round(perfData.reduce((a, b) => a + b.p50ResponseTime, 0) / perfData.length)
                      : 0}
                    <span className="text-sm font-normal">ms</span>
                  </p>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-4">
                  <p className="text-xs text-orange-600 font-medium mb-1">P95</p>
                  <p className="text-2xl font-bold text-orange-900">
                    {perfPeriod === 'allTime'
                      ? performanceMetrics.allTime.p95ResponseTime
                      : perfData.length > 0
                      ? Math.round(perfData.reduce((a, b) => a + b.p95ResponseTime, 0) / perfData.length)
                      : 0}
                    <span className="text-sm font-normal">ms</span>
                  </p>
                </div>
                <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-xl p-4">
                  <p className="text-xs text-red-600 font-medium mb-1">P99</p>
                  <p className="text-2xl font-bold text-red-900">
                    {perfPeriod === 'allTime'
                      ? performanceMetrics.allTime.p99ResponseTime
                      : perfData.length > 0
                      ? Math.round(perfData.reduce((a, b) => a + b.p99ResponseTime, 0) / perfData.length)
                      : 0}
                    <span className="text-sm font-normal">ms</span>
                  </p>
                </div>
              </div>

              {/* Response Time Chart */}
              {perfPeriod !== 'allTime' && perfData.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center gap-4 mb-2 text-xs">
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-purple-500"></span> Avg</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-teal-500"></span> P50</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-orange-500"></span> P95</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-500"></span> P99</span>
                  </div>
                  <div className="relative h-48 border-l border-b border-gray-200">
                    {/* Y-axis labels */}
                    <div className="absolute -left-10 top-0 text-xs text-gray-500">{maxPerfValue}ms</div>
                    <div className="absolute -left-10 top-1/2 text-xs text-gray-500">{Math.round(maxPerfValue / 2)}ms</div>
                    <div className="absolute -left-10 bottom-0 text-xs text-gray-500">0ms</div>

                    {/* Chart bars */}
                    <div className="flex items-end h-full gap-1 pl-2">
                      {perfData.map((point, index) => (
                        <div key={index} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                          {/* Tooltip */}
                          <div className="absolute bottom-full mb-2 hidden group-hover:block z-10 bg-gray-900 text-white text-xs rounded-lg p-2 whitespace-nowrap">
                            <p className="font-medium">{new Date(point.date).toLocaleDateString()}</p>
                            <p>Avg: {point.avgResponseTime}ms</p>
                            <p>P50: {point.p50ResponseTime}ms</p>
                            <p>P95: {point.p95ResponseTime}ms</p>
                            <p>P99: {point.p99ResponseTime}ms</p>
                            <p>Requests: {point.requestCount}</p>
                          </div>

                          {/* Stacked bars */}
                          <div className="w-full flex flex-col items-center gap-0.5" style={{ height: `${(point.p99ResponseTime / maxPerfValue) * 100}%` }}>
                            <div
                              className="w-full bg-red-400 rounded-t opacity-80"
                              style={{ height: `${((point.p99ResponseTime - point.p95ResponseTime) / point.p99ResponseTime) * 100}%`, minHeight: point.p99ResponseTime > point.p95ResponseTime ? '2px' : '0' }}
                            />
                            <div
                              className="w-full bg-orange-400 opacity-80"
                              style={{ height: `${((point.p95ResponseTime - point.p50ResponseTime) / point.p99ResponseTime) * 100}%`, minHeight: point.p95ResponseTime > point.p50ResponseTime ? '2px' : '0' }}
                            />
                            <div
                              className="w-full bg-teal-400 opacity-80"
                              style={{ height: `${((point.p50ResponseTime - point.avgResponseTime) / point.p99ResponseTime) * 100}%`, minHeight: point.p50ResponseTime > point.avgResponseTime ? '2px' : '0' }}
                            />
                            <div
                              className="w-full bg-purple-500 rounded-b"
                              style={{ height: `${(point.avgResponseTime / point.p99ResponseTime) * 100}%`, minHeight: '2px' }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* X-axis labels */}
                  <div className="flex justify-between mt-2 text-xs text-gray-500 pl-2">
                    {perfData.length > 0 && (
                      <>
                        <span>{new Date(perfData[0].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                        {perfData.length > 1 && (
                          <span>{new Date(perfData[perfData.length - 1].date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* All Time Stats */}
              {perfPeriod === 'allTime' && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-1">Min Response Time</p>
                    <p className="text-xl font-bold text-gray-900">{performanceMetrics.allTime.minResponseTime}ms</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-1">Max Response Time</p>
                    <p className="text-xl font-bold text-gray-900">{performanceMetrics.allTime.maxResponseTime}ms</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs text-gray-500 mb-1">Total Requests</p>
                    <p className="text-xl font-bold text-gray-900">{performanceMetrics.allTime.totalRequests.toLocaleString()}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Logs Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Request Logs
                  {selectedUserId && (
                    <span className="ml-2 text-sm font-normal text-gray-500">
                      (Filtered by user)
                    </span>
                  )}
                </h3>
                <div className="flex items-center gap-3">
                  <select
                    value={selectedUserId}
                    onChange={(e) => {
                      setSelectedUserId(e.target.value);
                      setLogsPage(0);
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">All Users</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </option>
                    ))}
                  </select>
                  {selectedUserId && (
                    <button
                      onClick={() => {
                        setSelectedUserId('');
                        setLogsPage(0);
                      }}
                      className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                    >
                      Clear Filter
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('timestamp')}
                    >
                      <div className="flex items-center gap-1">
                        Timestamp
                        {getSortIcon('timestamp')}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('endpoint')}
                    >
                      <div className="flex items-center gap-1">
                        Endpoint
                        {getSortIcon('endpoint')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('status_code')}
                    >
                      <div className="flex items-center gap-1">
                        Status
                        {getSortIcon('status_code')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('response_time_ms')}
                    >
                      <div className="flex items-center gap-1">
                        Response Time
                        {getSortIcon('response_time_ms')}
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IP Address</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {logs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                        No logs found
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatTimestamp(log.timestamp)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {log.userName ? (
                            <div>
                              <p className="text-sm font-medium text-gray-900">{log.userName}</p>
                              <p className="text-xs text-gray-500">{log.userEmail}</p>
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">Anonymous</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-mono text-gray-700 break-all max-w-xs block">
                            {log.endpoint}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(log.statusCode)}`}>
                            {log.statusCode || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {log.responseTimeMs !== null ? `${log.responseTimeMs}ms` : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                          {log.ipAddress}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {logs.length} of {totalLogs.toLocaleString()} logs
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setLogsPage(Math.max(0, logsPage - 1))}
                  disabled={logsPage === 0}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">
                  Page {logsPage + 1} of {Math.max(1, Math.ceil(totalLogs / logsPerPage))}
                </span>
                <button
                  onClick={() => setLogsPage(logsPage + 1)}
                  disabled={(logsPage + 1) * logsPerPage >= totalLogs}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
